// OWNER: FRONTEND-ADMIN. Campaign page sections: preferred windows, setter assignment, members table, Calendly event field.
import { useEffect, useState } from 'preact/hooks';
import type { Campaign, MembershipStatus, PreferredWindow } from '../../../src/shared/api';
import type { MemberRow } from '../../../src/shared/api-core';
import { errorMessage } from '../api';
import { ErrorBanner, fmtDateTime, timeAgo, toast } from '../components/ui';
import { adminApi } from './endpoints';
import { formatPhone } from './phone';
import { Check, Field, ListEditor, ProblemList, inputValue, useLoad, useUnsavedWarning } from './ui';
import {
  CALENDLY_ACCOUNT_URL, CALENDLY_EVENTS, DEFAULT_CALENDLY_SLUG, calendlySlugProblems, normalizeCalendlySlug, preferredWindowProblems,
} from './validate';

const DAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function WindowsEditor(props: { campaign: Campaign; onSaved: (c: Campaign) => void }) {
  const [windows, setWindows] = useState<PreferredWindow[]>(props.campaign.windows ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = JSON.stringify(windows) !== JSON.stringify(props.campaign.windows ?? []);
  useUnsavedWarning(dirty);
  useEffect(() => { setWindows(props.campaign.windows ?? []); }, [props.campaign.id, props.campaign.updatedAt]);
  const problems = windows.flatMap((w, i) => preferredWindowProblems(w, i));

  const save = async () => {
    if (problems.length) { setError(problems[0]); return; }
    setBusy(true); setError(null);
    try {
      const c = await adminApi.updateCampaign(props.campaign.id, { windows });
      props.onSaved(c);
      toast('Calling windows saved.', 'ok');
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div class="card stack">
      <h3>Preferred calling windows</h3>
      <div class="adm-sub">
        When this niche is most likely to pick up (prospect-local time). The queue puts leads in a preferred window first.
        This is a preference, not the law: the legal calling hours and holidays are enforced separately and always win.
      </div>
      <ListEditor<PreferredWindow>
        items={windows} onChange={setWindows} addLabel="Add window"
        make={() => ({ days: [1, 2, 3, 4, 5], start: '09:00', end: '11:00', label: '' })}
        title={(w) => `${w.label || '(no label)'} · ${w.days.map((d) => DAY_NAMES[d] ?? '?').join(' ')} ${w.start}–${w.end}`}
        render={(w, update) => (
          <div class="stack">
            <div class="adm-grid">
              <Field label="Label"><input value={w.label} placeholder="Owner before dispatch" onInput={(e) => update({ ...w, label: inputValue(e) })} /></Field>
              <Field label="Start (prospect-local)"><input type="time" value={w.start} onInput={(e) => update({ ...w, start: inputValue(e) })} /></Field>
              <Field label="End"><input type="time" value={w.end} onInput={(e) => update({ ...w, end: inputValue(e) })} /></Field>
            </div>
            <div class="row" style="flex-wrap:wrap;gap:12px">
              {[1, 2, 3, 4, 5, 6].map((d) => (
                <Check label={DAY_NAMES[d]} checked={w.days.includes(d)}
                  onChange={(on) => update({ ...w, days: on ? [...w.days, d].sort((a, b) => a - b) : w.days.filter((x) => x !== d) })} />
              ))}
            </div>
          </div>
        )}
      />
      {problems.length ? <ul class="adm-problems">{problems.map((p) => <li class="error">{p}</li>)}</ul> : null}
      <ErrorBanner error={error} />
      <div class="row">
        <button class="btn btn-primary" disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save windows'}</button>
        <button class="btn" disabled={!dirty || busy} onClick={() => setWindows(props.campaign.windows ?? [])}>Revert</button>
      </div>
    </div>
  );
}

export function SettersPanel(props: { campaign: Campaign; onSaved: (c: Campaign) => void }) {
  const users = useLoad(() => adminApi.users(), []);
  const [selected, setSelected] = useState<number[]>(props.campaign.userIds ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setSelected(props.campaign.userIds ?? []); }, [props.campaign.id, props.campaign.updatedAt]);
  const dirty = [...selected].sort().join(',') !== [...(props.campaign.userIds ?? [])].sort().join(',');
  useUnsavedWarning(dirty);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const c = await adminApi.setCampaignUsers(props.campaign.id, selected);
      props.onSaved(c);
      toast('Setters saved.', 'ok');
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  const list = (users.data ?? []).filter((u) => u.active || selected.includes(u.id));
  return (
    <div class="card stack">
      <h3>Assigned setters</h3>
      <div class="adm-sub">Setters only see and dial campaigns they're assigned to. Each still has to pass certification for this campaign (Training) unless they're certify-exempt.</div>
      <ErrorBanner error={users.error} />
      {users.data && list.length === 0 ? <div class="muted">No active users yet. Add setters in Settings → Users.</div> : null}
      <div class="stack" style="gap:6px">
        {list.map((u) => (
          <Check checked={selected.includes(u.id)} onChange={(on) => setSelected((cur) => (on ? [...cur.filter((x) => x !== u.id), u.id] : cur.filter((x) => x !== u.id)))}
            label={<span>{u.name} <span class="muted">· {u.email} · {u.role}{u.certifyExempt ? ' · certify-exempt' : ''}{u.active ? '' : ' · INACTIVE'}</span></span>} />
        ))}
      </div>
      <ErrorBanner error={error} />
      <div class="row">
        <button class="btn btn-primary" disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save setters'}</button>
        <span class="adm-sub">{selected.length} assigned</span>
      </div>
    </div>
  );
}

const MEMBER_STATUSES: MembershipStatus[] = ['new', 'in_progress', 'callback', 'meeting', 'closed_lost', 'disqualified', 'bad_data', 'dnc', 'exhausted'];
const PAGE = 100;

export function MembersTable(props: { campaign: Campaign }) {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const rows = useLoad(() => adminApi.members(props.campaign.id, { status: status || undefined, q: query || undefined, limit: PAGE, offset }), [props.campaign.id, status, query, offset]);
  const total = rows.data?.total ?? 0;
  const items: MemberRow[] = rows.data?.items ?? [];

  return (
    <div class="card stack">
      <div class="adm-toolbar">
        <h3 style="margin:0">Leads in this campaign</h3>
        <span class="grow" />
        <select value={status} onChange={(e) => { setStatus(inputValue(e)); setOffset(0); }}>
          <option value="">Every status</option>
          {MEMBER_STATUSES.map((s) => <option value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <form class="row" onSubmit={(e) => { e.preventDefault(); setQuery(q.trim()); setOffset(0); }}>
          <input placeholder="Name, phone or city" value={q} onInput={(e) => setQ(inputValue(e))} />
          <button class="btn btn-sm" type="submit">Search</button>
        </form>
      </div>
      <ErrorBanner error={rows.error} />
      <div class="adm-table-wrap" style="max-height:60vh">
        <table>
          <thead><tr>
            <th>Business</th><th>Phone</th><th>State</th><th>Status</th><th class="num">Attempts</th><th class="num">VMs</th>
            <th>Last attempt</th><th>Next eligible</th><th>Line</th><th>Flags</th>
          </tr></thead>
          <tbody>
            {items.map((m) => {
              const b = m.business;
              const flags = [
                b.isHealthcare && b.country === 'CA' ? 'healthcare' : '',
                b.doNotContact ? 'DNC' : '',
                b.badNumber ? 'bad number' : '',
                m.lockedBy && m.lockedUntil && new Date(m.lockedUntil) > new Date() ? 'locked' : '',
              ].filter(Boolean);
              return (
                <tr class={['closed_lost', 'disqualified', 'bad_data', 'dnc', 'exhausted'].includes(m.status) ? 'adm-dim' : ''}>
                  <td><b>{b.name}</b>{b.city ? <span class="muted"> · {b.city}</span> : null}</td>
                  <td class="mono">{formatPhone(b.phoneE164)}</td>
                  <td>{b.country}-{b.stateProv}</td>
                  <td><span class="chip">{m.status.replace(/_/g, ' ')}</span></td>
                  <td class="num">{m.attempts}/{props.campaign.maxAttempts}</td>
                  <td class="num">{m.vmCount}/{props.campaign.maxVms}</td>
                  <td title={fmtDateTime(m.lastAttemptAt)}>{timeAgo(m.lastAttemptAt)}</td>
                  <td>{m.nextEligibleAt ? fmtDateTime(m.nextEligibleAt) : '—'}</td>
                  <td>{b.lineType ?? <span class="muted">unknown</span>}</td>
                  <td>{flags.map((f) => <span class="chip chip-warn" style="margin-right:4px">{f}</span>)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.data && items.length === 0 ? <div class="muted">No leads match.</div> : null}
      <div class="row" style="justify-content:space-between">
        <span class="adm-sub">{rows.loading ? 'Loading…' : `${total === 0 ? 0 : offset + 1}–${offset + items.length} of ${total.toLocaleString()}`}</span>
        <div class="row">
          <button class="btn btn-sm" disabled={offset === 0 || rows.loading} onClick={() => setOffset(Math.max(0, offset - PAGE))}>← Previous</button>
          <button class="btn btn-sm" disabled={offset + items.length >= total || rows.loading} onClick={() => setOffset(offset + PAGE)}>Next →</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Calendly event (SPEC §10, §15.5)

/**
 * The Calendly event setters book into for this campaign. Accepts a pasted link (normalised to the slug on blur),
 * shows which Valistix event it is, a link to check it, and the template's default with a one-click reset.
 * `templateDefault` null = no template (blank campaign), so the app default applies. `niche` (the campaign's) turns on
 * the strict cannabis lock: a cannabis campaign on any link but your-store, or another campaign on your-store, is an
 * error that blocks saving (APPROVAL-three-links 6.3).
 */
export function CalendlySlugField(props: { value: string; onChange: (raw: string) => void; templateDefault: string | null; templateName?: string | null; niche?: string | null }) {
  const slug = normalizeCalendlySlug(props.value);
  const problems = calendlySlugProblems(slug, props.niche);
  const valid = !problems.some((p) => p.level === 'error');
  const known = CALENDLY_EVENTS.find((e) => e.slug === slug);
  const fallback = props.templateDefault ?? DEFAULT_CALENDLY_SLUG;
  return (
    <div class="stack" style="gap:4px">
      <Field label="Calendly event (booking link)">
        <input list="vd-calendly-events" value={props.value} placeholder={fallback}
          onInput={(e) => props.onChange(inputValue(e))}
          onBlur={() => { if (slug && slug !== props.value) props.onChange(slug); }} />
      </Field>
      <datalist id="vd-calendly-events">{CALENDLY_EVENTS.map((e) => <option value={e.slug}>{e.name} ({e.note})</option>)}</datalist>
      <div class="adm-hint" style="font-size:12px;color:var(--ink-3)">
        {valid ? (
          <>
            {known ? <>“{known.name}”, {known.note}. </> : null}
            Setters book into{' '}
            <a href={`${CALENDLY_ACCOUNT_URL}/${encodeURIComponent(slug)}`} target="_blank" rel="noopener noreferrer">{CALENDLY_ACCOUNT_URL.replace(/^https:\/\//, '')}/{slug}</a>.{' '}
          </>
        ) : null}
        {props.templateDefault !== null
          ? <>Template default{props.templateName ? ` (${props.templateName})` : ''}: <code>{props.templateDefault}</code>.</>
          : <>Default: <code>{DEFAULT_CALENDLY_SLUG}</code>.</>}
        {slug !== fallback ? <>{' '}<button type="button" class="adm-link" onClick={() => props.onChange(fallback)}>Use {fallback}</button></> : null}
      </div>
      <ProblemList problems={problems} />
    </div>
  );
}
